// src/UserEntry.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BrowserMultiFormatReader } from '@zxing/library';

export default function UserEntry() {
  const [error, setError] = useState('');
  const [successSound] = useState(() => new Audio('/sounds/success.mp3'));
  const [scanActive, setScanActive] = useState(true);
  const videoRef = useRef(null);
  const codeReader = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    codeReader.current = new BrowserMultiFormatReader();
    if (scanActive) {
      codeReader.current.decodeFromVideoDevice(
        null,
        videoRef.current,
        (result, err) => {
          if (result) {
            const scannedBarcode = result.getText();
            successSound.play();
            codeReader.current?.reset();
            navigate(`/user/${scannedBarcode}`);
          } else if (err && err.name !== 'NotFoundException') {
            console.error(err);
            setError('حدث خطأ أثناء قراءة الباركود.');
          }
        }
      );
    }

    return () => {
      try {
        codeReader.current?.reset();
      } catch (e) {
        console.error('فشل إيقاف الكاميرا:', e);
      }
    };
  }, [navigate, scanActive, successSound]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.src = reader.result;
      img.onload = async () => {
        try {
          const result = await codeReader.current.decodeFromImage(img);
          const imgBarcode = result.getText();
          successSound.play();
          navigate(`/user/${imgBarcode}`);
        } catch (err) {
          console.error(err);
          setError('تعذر قراءة الباركود من الصورة.');
        }
      };
    };
    reader.readAsDataURL(file);
  };

  const handleRetry = () => {
    setError('');
    setScanActive(false);
    setTimeout(() => setScanActive(true), 200);
  };

  return (
    <div
      dir="rtl"
      className="min-h-screen flex items-center justify-center bg-gradient-to-tr from-yellow-50 to-white p-6 sm:p-12"
    >
      <AnimatePresence>
        <motion.div
          className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 sm:p-10 w-full max-w-lg border border-yellow-200 shadow-[0_10px_30px_rgba(250,204,21,.2)] text-right"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          transition={{ duration: 0.6 }}
        >
          <motion.h2
            className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2"
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.25 }}
          >
            📸 تسجيل دخول المشترك
          </motion.h2>

          <p className="text-gray-700 mb-6">
            امسح الباركود بالكاميرا أو ارفع صورة من جهازك.
          </p>

          {error && (
            <motion.div
              className="mb-5 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 p-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {error}
              <button
                onClick={handleRetry}
                className="mt-3 inline-flex items-center gap-1 px-4 py-1.5 rounded-full text-sm font-semibold border border-amber-300 bg-amber-100 hover:bg-amber-200 text-amber-900 transition"
              >
                🔁 إعادة المحاولة
              </button>
            </motion.div>
          )}

          {scanActive && (
            <motion.div
              className="relative rounded-2xl overflow-hidden mb-6 shadow-lg"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.35 }}
            >
              {/* فيديو الكاميرا */}
              <video ref={videoRef} className="w-full h-64 object-cover" />

              {/* طبقة شفافة لإعطاء عمق */}
              <div className="absolute inset-0 bg-black/20 backdrop-blur-[1px]" />

              {/* زوايا مضيئة */}
              <div className="absolute top-3 left-3 w-8 h-8 border-t-4 border-l-4 border-yellow-400 rounded-tl-lg shadow-[0_0_15px_rgba(250,204,21,0.8)]"></div>
              <div className="absolute top-3 right-3 w-8 h-8 border-t-4 border-r-4 border-yellow-400 rounded-tr-lg shadow-[0_0_15px_rgba(250,204,21,0.8)]"></div>
              <div className="absolute bottom-3 left-3 w-8 h-8 border-b-4 border-l-4 border-yellow-400 rounded-bl-lg shadow-[0_0_15px_rgba(250,204,21,0.8)]"></div>
              <div className="absolute bottom-3 right-3 w-8 h-8 border-b-4 border-r-4 border-yellow-400 rounded-br-lg shadow-[0_0_15px_rgba(250,204,21,0.8)]"></div>

              {/* خط المسح */}
              <div className="absolute inset-x-6 top-6 h-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent animate-scanLine rounded-full shadow-[0_0_20px_rgba(250,204,21,0.9)]" />
            </motion.div>
          )}

          <label className="block mb-2 text-gray-800 font-medium">
            📷 اختر صورة من جهازك:
          </label>

          <motion.input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="block w-full text-sm text-gray-700
              file:mr-0 file:ml-4 file:py-2 file:px-5
              file:rounded-full file:border file:border-yellow-300
              file:text-sm file:font-bold
              file:bg-yellow-100 file:text-gray-900
              hover:file:bg-yellow-200 cursor-pointer transition"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.45 }}
          />

          <div className="mt-6 text-[12px] text-gray-500">
            ملاحظة: تأكّد من إضاءة جيدة ووضوح الباركود للحصول على نتائج أدق.
          </div>
        </motion.div>
      </AnimatePresence>

      <style>{`
        @keyframes scanLine {
          0% { transform: translateY(0%); opacity: 0.8; }
          50% { transform: translateY(calc(100% - 2rem)); opacity: 1; }
          100% { transform: translateY(0%); opacity: 0.8; }
        }
        .animate-scanLine {
          animation: scanLine 3s infinite ease-in-out;
        }
      `}</style>
    </div>
  );
}
